import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { dishes } from "../data/dishes";

const SiteContext = createContext(null);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function SiteProvider({ children }) {
  const [theme, setThemeState] = useState({ colour: dishes[0].colour, pale: dishes[0].pale });
  const [modal, setModal] = useState(null); // { type: 'order'|'gallery', dishId?, index? }
  const [paused, setPaused] = useState(() => prefersReducedMotion());
  const [reduced, setReduced] = useState(() => prefersReducedMotion());
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => {
      setReduced(e.matches);
      if (e.matches) setPaused(true);
    };
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("ro-paused", paused);
    return () => document.documentElement.classList.remove("ro-paused");
  }, [paused]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme.colour);
  }, [theme]);

  const setTheme = useCallback((dish) => {
    const next = dish ? { colour: dish.colour, pale: dish.pale } : { colour: dishes[0].colour, pale: dishes[0].pale };
    setThemeState((prev) => (prev.colour === next.colour && prev.pale === next.pale ? prev : next));
  }, []);

  const openModal = useCallback((type, extra = {}) => setModal({ type, ...extra }), []);
  const closeModal = useCallback(() => setModal(null), []);

  const value = useMemo(
    () => ({ theme, setTheme, modal, openModal, closeModal, paused, setPaused, reduced, navOpen, setNavOpen }),
    [theme, setTheme, modal, openModal, closeModal, paused, reduced, navOpen]
  );
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used inside SiteProvider");
  return ctx;
}

// Pages call this to colour the shared shell (header, buttons, outlines).
export function useTheme(dish) {
  const { setTheme } = useSite();
  useEffect(() => {
    setTheme(dish || null);
  }, [dish, setTheme]);
}

export function useDocumentTitle(title) {
  useEffect(() => {
    document.title = title ? `RollOver | ${title}` : "RollOver | Good food. Good mood.";
  }, [title]);
}
